<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Header with actions -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto">
        <v-select
          v-model="selectedPlatformId"
          :items="platforms"
          item-title="name"
          item-value="id"
          label="Платформа"
          variant="outlined"
          density="compact"
          hide-details
          style="min-width: 200px"
          @update:model-value="loadSections"
        ></v-select>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Рубрики платформ</h4>
      </v-col>
      <v-col cols="auto" v-if="sections.length > 0">
        <v-btn
          variant="outlined"
          size="small"
          :loading="bulkToggling"
          @click="bulkToggle(true)"
          class="mr-2"
        >
          Включить все
        </v-btn>
        <v-btn
          variant="outlined"
          size="small"
          :loading="bulkToggling"
          @click="bulkToggle(false)"
        >
          Выключить все
        </v-btn>
      </v-col>
    </v-row>

    <!-- Table section -->
    <v-card v-if="selectedPlatformId">
      <v-card-text class="pa-0">
        <v-data-table
          v-if="sections.length > 0"
          :headers="tableHeaders"
          :items="flatSections"
          :loading="loading"
          class="elevation-1"
          hover
          fixed-header
          height="calc(100vh - 276px)"
          hide-default-footer
          items-per-page="-1"
          item-value="id"
        >
          <template v-slot:[`item.name`]="{ item }">
            <div v-if="item._isChild" class="pl-4">
              <v-icon size="small" class="mr-1 text-grey">mdi-subdirectory-arrow-right</v-icon>
              {{ item.name }}
            </div>
            <strong v-else>{{ item.name }}</strong>
          </template>

          <template v-slot:[`item.code`]="{ item }">
            <span class="text-caption text-grey">{{ item.code }}</span>
          </template>

          <template v-slot:[`item.url`]="{ item }">
            <a :href="item.url" target="_blank" class="text-decoration-none text-caption">
              {{ shortenUrl(item.url) }}
              <v-icon size="x-small" class="ml-1">mdi-open-in-new</v-icon>
            </a>
          </template>

          <template v-slot:[`item.is_active`]="{ item }">
            <v-switch
              :model-value="item.is_active"
              color="success"
              hide-details
              density="compact"
              :loading="item._toggling"
              @update:model-value="toggleSection(item, $event)"
            ></v-switch>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-6">
              <v-icon size="64" color="grey-lighten-2">mdi-tag-off-outline</v-icon>
              <p class="text-h6 text-grey-darken-1 mt-4">Нет рубрик для выбранной платформы</p>
            </div>
          </template>
        </v-data-table>
        <div v-else class="text-center py-6">
          <v-icon size="64" color="grey-lighten-2">mdi-tag-off-outline</v-icon>
          <p class="text-h6 text-grey-darken-1 mt-4">Нет рубрик для выбранной платформы</p>
        </div>
      </v-card-text>
    </v-card>
    <v-card v-else>
      <v-card-text>
        <div class="text-center py-6">
          <v-icon size="64" color="grey-lighten-2">mdi-tag-multiple-outline</v-icon>
          <p class="text-h6 text-grey-darken-1 mt-4">Выберите платформу</p>
        </div>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="2000" location="bottom right">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'
import { referenceApi } from '@/services/api/referenceApi'

export default {
  name: 'PlatformSectionsTable',

  data() {
    return {
      platforms: [],
      selectedPlatformId: null,
      sections: [],
      loading: false,
      bulkToggling: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      tableHeaders: [
        { title: 'Рубрика', key: 'name', sortable: false },
        { title: 'Код', key: 'code', sortable: false, width: 150 },
        { title: 'URL', key: 'url', sortable: false },
        { title: 'Мониторинг', key: 'is_active', sortable: false, width: 140, align: 'center' },
      ],
    }
  },

  computed: {
    flatSections() {
      const result = []
      for (const section of this.sections) {
        result.push(section)
        for (const child of (section.children || [])) {
          result.push({ ...child, _isChild: true })
        }
      }
      return result
    },
  },

  async created() {
    await this.loadPlatforms()
  },

  methods: {
    async loadPlatforms() {
      try {
        const response = await referenceApi.getPlatforms({ skip: 0, limit: 100 })
        this.platforms = response.data?.items || response.data || []
        // Auto-select first platform that has sections (Dzen = id 12)
        const dzen = this.platforms.find(p => p.name === 'dzen' || p.id === 12)
        if (dzen) {
          this.selectedPlatformId = dzen.id
          await this.loadSections()
        }
      } catch (e) {
        this.showSnackbar('Ошибка загрузки платформ', 'error')
      }
    },

    async loadSections() {
      if (!this.selectedPlatformId) {
        this.sections = []
        return
      }
      this.loading = true
      try {
        const { data } = await apiClient.get(`/v2/platform-sections/${this.selectedPlatformId}`)
        this.sections = data.items.map(s => ({
          ...s,
          _toggling: false,
          children: (s.children || []).map(c => ({ ...c, _toggling: false })),
        }))
      } catch (e) {
        this.showSnackbar('Ошибка загрузки рубрик', 'error')
      } finally {
        this.loading = false
      }
    },

    async toggleSection(section, isActive) {
      section._toggling = true
      try {
        await apiClient.patch(
          `/v2/platform-sections/${this.selectedPlatformId}/${section.id}/toggle`,
          { is_active: isActive }
        )
        section.is_active = isActive
      } catch (e) {
        this.showSnackbar('Ошибка переключения', 'error')
      } finally {
        section._toggling = false
      }
    },

    async bulkToggle(isActive) {
      this.bulkToggling = true
      try {
        await apiClient.patch(
          `/v2/platform-sections/${this.selectedPlatformId}/bulk-toggle`,
          { is_active: isActive }
        )
        await this.loadSections()
        this.showSnackbar(isActive ? 'Все рубрики включены' : 'Все рубрики выключены')
      } catch (e) {
        this.showSnackbar('Ошибка', 'error')
      } finally {
        this.bulkToggling = false
      }
    },

    shortenUrl(url) {
      return url.replace('https://', '').replace('http://', '')
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
.v-data-table {
  font-size: 0.875rem;
}

.v-data-table :deep(.v-data-table__td) {
  padding: 8px 16px !important;
  height: 60px !important;
}

.v-data-table :deep(.v-data-table__th) {
  padding: 8px 16px !important;
  font-weight: 600;
  white-space: nowrap;
}
</style>
